import { useState, useEffect, useCallback, useRef } from 'react';
import { LogOut, Settings, ShoppingBag, Printer, Clock, CheckCircle, XCircle, RefreshCw, Eye, BarChart3, ChevronLeft, ChevronRight, UtensilsCrossed, Search, Star, Trash2, Tag, Plus, MessageCircle, AlertTriangle, FileText, Download, ShieldCheck, CreditCard, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

import { LOGO_BASE64 } from '@/utils/logoData';
import { printOrderReceipt, printDailySalesReport, setPrintNotifier } from '@/utils/printReceipt';
import { probeQz } from '@/utils/qzPrinter';
import { openWhatsAppPane, sendToWhatsApp } from '@/utils/whatsappPane';

const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [settings, setSettings] = useState({ online_ordering_enabled: true, whatsapp_ordering_enabled: true });
  const [orders, setOrders] = useState([]);
  const [businessDate, setBusinessDate] = useState(null);   // YYYY-MM-DD, set from response header
  const [ordersDate, setOrdersDate] = useState('');          // selected date filter ('' = today's business day)
  // CSV export for gestor
  const [showExport, setShowExport] = useState(false);
  const [exportMonth, setExportMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  const [allergens, setAllergens] = useState([]);            // [{key, label}, ...]
  const [allergenItems, setAllergenItems] = useState([]);    // [{id, name, category}, ...]
  const [allergenMatrix, setAllergenMatrix] = useState({});  // {item_id: [keys]}
  const [allergenSaving, setAllergenSaving] = useState(false);
  const [allergenSearch, setAllergenSearch] = useState('');
  const [allergenCategory, setAllergenCategory] = useState('Todos');
  // VeriFactu / Facturación (RD 1007/2023)
  const [fiscal, setFiscal] = useState({ nif: '', razon_social: '', direccion: '', codigo_postal: '', municipio: '', provincia: '', mode: 'no-verifactu', configured: false });
  const [fiscalSaving, setFiscalSaving] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [ticketChain, setTicketChain] = useState({ next_seq: 1, year: new Date().getFullYear() });
  const [ticketsDateFrom, setTicketsDateFrom] = useState('');
  const [ticketsDateTo, setTicketsDateTo] = useState('');
  const [chainStatus, setChainStatus] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [activeTab, setActiveTab] = useState('orders');
  const [waMenuFor, setWaMenuFor] = useState(null);  // order_id whose WhatsApp template menu is open

  // Close the WhatsApp template dropdown when clicking anywhere outside it.
  useEffect(() => {
    if (!waMenuFor) return;
    const onDown = (e) => {
      const menu = document.querySelector(`[data-testid="wa-templates-${waMenuFor}"]`);
      const btn = document.querySelector(`[data-testid="whatsapp-menu-${waMenuFor}"]`);
      if (menu && !menu.contains(e.target) && btn && !btn.contains(e.target)) {
        setWaMenuFor(null);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [waMenuFor]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const prevOrderCount = useRef(0);
  const [reportMonth, setReportMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [frozenIds, setFrozenIds] = useState([]);
  const [menuSearch, setMenuSearch] = useState('');
  const [menuCategory, setMenuCategory] = useState('Todos');
  const [dailyDate, setDailyDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [dailyReport, setDailyReport] = useState(null);
  // Custom date-range sales report (independent of the monthly view)
  const [rangeFrom, setRangeFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [rangeTo, setRangeTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [rangeReport, setRangeReport] = useState(null);
  const [rangeLoading, setRangeLoading] = useState(false);
  // "Pagos pendientes" tab state
  const [pendingPayments, setPendingPayments] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [recoverRef, setRecoverRef] = useState('');

  const getAuthHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/login`, { email, password }, { withCredentials: true });
      setToken(response.data.token);
      setIsLoggedIn(true);
      toast.success('Sesion iniciada');
    } catch {
      toast.error('Credenciales incorrectas');
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setToken('');
    axios.post(`${API}/admin/logout`, {}, { withCredentials: true });
  };

  // ---------- New-order looping notification tone ----------
  // Plays the mp3 on repeat as long as there is at least one 'nuevo' order.
  // Stops automatically when every order has been accepted or cancelled/rejected.
  const notifAudioRef = useRef(null);
  const audioUnlockedRef = useRef(false);

  // Unlock audio on first user interaction (browsers block autoplay until click)
  useEffect(() => {
    if (!isLoggedIn) return;
    const unlock = () => {
      if (audioUnlockedRef.current) return;
      audioUnlockedRef.current = true;
      if (notifAudioRef.current) {
        // Try a silent play+pause to obtain the autoplay permission
        notifAudioRef.current.volume = 0;
        notifAudioRef.current.play().then(() => {
          notifAudioRef.current.pause();
          notifAudioRef.current.currentTime = 0;
          notifAudioRef.current.volume = 1;
        }).catch(() => {});
      }
    };
    window.addEventListener('click', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('click', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, [isLoggedIn]);

  // Start/stop loop based on presence of any 'nuevo' order OR pending Redsys payment
  useEffect(() => {
    if (!isLoggedIn) return;
    const hasNuevo = orders.some((o) => o.status === 'nuevo');
    const hasPending = pendingPayments.length > 0;
    const audio = notifAudioRef.current;
    if (!audio) return;
    if (hasNuevo || hasPending) {
      if (audio.paused) {
        audio.loop = true;
        audio.volume = 1;
        audio.play().catch(() => {
          // Blocked by autoplay policy — will start on next user click via unlock effect.
        });
      }
    } else {
      if (!audio.paused) {
        audio.pause();
        audio.currentTime = 0;
      }
    }
  }, [orders, pendingPayments, isLoggedIn]);

  // Retry playing the tone on ANY user click while it should be playing.
  // Fixes cases where the browser's autoplay policy blocked the first attempt.
  useEffect(() => {
    if (!isLoggedIn) return;
    const retry = () => {
      const audio = notifAudioRef.current;
      if (!audio) return;
      const shouldPlay = orders.some((o) => o.status === 'nuevo') || pendingPayments.length > 0;
      if (shouldPlay && audio.paused) {
        audio.loop = true;
        audio.volume = 1;
        audio.play().catch(() => {});
      }
    };
    window.addEventListener('click', retry);
    window.addEventListener('keydown', retry);
    return () => {
      window.removeEventListener('click', retry);
      window.removeEventListener('keydown', retry);
    };
  }, [isLoggedIn, orders, pendingPayments]);

  const fetchOrders = useCallback(async () => {
    if (!token) return;
    try {
      const url = ordersDate ? `${API}/admin/orders?date=${ordersDate}` : `${API}/admin/orders`;
      const response = await axios.get(url, getAuthHeaders());
      const newOrders = response.data;
      const newNuevoCount = newOrders.filter((o) => o.status === 'nuevo').length;
      const prevNuevoCount = orders.filter((o) => o.status === 'nuevo').length;
      if (newNuevoCount > prevNuevoCount) {
        toast.info('Nuevo pedido recibido! Aceptar en 5 minutos');
      }
      prevOrderCount.current = newOrders.length;
      setOrders(newOrders);
      const bd = response.headers['x-business-date'] || response.headers['X-Business-Date'];
      if (bd) setBusinessDate(bd);
    } catch {}
  }, [token, getAuthHeaders, orders, ordersDate]);

  const fetchSettings = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch {}
  }, [token]);

  const fetchMenu = useCallback(async () => {
    if (!token) return;
    try {
      const [menuRes, frozenRes] = await Promise.all([
        axios.get(`${API}/menu`),
        axios.get(`${API}/admin/menu/frozen`, getAuthHeaders())
      ]);
      setMenuItems(menuRes.data);
      setFrozenIds(frozenRes.data.frozen_ids || []);
    } catch {}
  }, [token, getAuthHeaders]);

  const toggleMenuItem = async (itemId) => {
    try {
      const res = await axios.post(`${API}/admin/menu/${itemId}/toggle`, {}, getAuthHeaders());
      setFrozenIds(res.data.frozen_ids);
      toast.success(res.data.message);
    } catch {
      toast.error('Error al actualizar');
    }
  };

  useEffect(() => {
    if (!isLoggedIn) return;
    fetchOrders();
    fetchSettings();
    fetchMenu();
    fetchPendingPayments(true);
    const interval = setInterval(fetchOrders, 10000);
    const pendingInterval = setInterval(() => fetchPendingPayments(true), 20000);
    return () => { clearInterval(interval); clearInterval(pendingInterval); };
  }, [isLoggedIn, fetchOrders, fetchSettings, fetchMenu]);

  // QZ Tray connection probe + toast notifier wiring
  const [qzStatus, setQzStatus] = useState('checking'); // 'ok' | 'offline' | 'checking'
  useEffect(() => {
    if (!isLoggedIn) return;
    setPrintNotifier((type, msg) => (type === 'success' ? toast.success(msg) : toast.error(msg)));
    let mounted = true;
    const check = async () => {
      const ok = await probeQz();
      if (mounted) setQzStatus(ok ? 'ok' : 'offline');
    };
    check();
    const id = setInterval(check, 15000);
    return () => { mounted = false; clearInterval(id); };
  }, [isLoggedIn]);

  // ---------- Discount codes admin ----------
  const [discountCodes, setDiscountCodes] = useState([]);
  const [discountForm, setDiscountForm] = useState({
    code: '', description: '', percentage: 5, code_type: 'new_customer', min_order_amount: 0, active: true,
  });
  const fetchDiscountCodes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/discount-codes`, getAuthHeaders());
      setDiscountCodes(data);
    } catch {}
  }, [getAuthHeaders]);
  const createDiscountCode = async () => {
    if (!discountForm.code.trim()) { toast.error('Introduce un codigo'); return; }
    try {
      await axios.post(`${API}/admin/discount-codes`, discountForm, getAuthHeaders());
      toast.success('Codigo creado');
      setDiscountForm({ code: '', description: '', percentage: 5, code_type: 'new_customer', min_order_amount: 0, active: true });
      fetchDiscountCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error creando codigo');
    }
  };
  const toggleDiscountCode = async (id) => {
    try {
      await axios.post(`${API}/admin/discount-codes/${id}/toggle`, {}, getAuthHeaders());
      fetchDiscountCodes();
    } catch {}
  };
  const deleteDiscountCode = async (id) => {
    if (!window.confirm('¿Eliminar este codigo?')) return;
    try {
      await axios.delete(`${API}/admin/discount-codes/${id}`, getAuthHeaders());
      toast.success('Codigo eliminado');
      fetchDiscountCodes();
    } catch {}
  };

  // ---------- Allergen matrix admin ----------
  const fetchAllergenMatrix = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/allergens`);
      setAllergens(data.allergens || []);
      setAllergenItems(data.items || []);
      setAllergenMatrix(data.matrix || {});
    } catch {}
  }, []);
  const toggleAllergen = (itemId, key) => {
    setAllergenMatrix(prev => {
      const cur = new Set(prev[itemId] || []);
      if (cur.has(key)) cur.delete(key); else cur.add(key);
      return { ...prev, [itemId]: Array.from(cur) };
    });
  };
  const saveAllergenMatrix = async () => {
    setAllergenSaving(true);
    try {
      await axios.put(`${API}/admin/allergens`, { matrix: allergenMatrix }, getAuthHeaders());
      toast.success('Matriz de alergenos guardada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setAllergenSaving(false);
    }
  };
  const exportAllergenMatrix = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/allergens/export`, getAuthHeaders());
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `tajmahal-alergenos-${stamp}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Exportado. Subelo en la otra instalacion con "Importar JSON".');
    } catch (err) {
      toast.error('Error al exportar');
    }
  };
  const importAllergenMatrix = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so re-selecting same file fires onChange
    if (!file) return;
    if (!window.confirm('Esto sobrescribira la matriz actual con la del archivo. ¿Continuar?')) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const matrix = parsed?.matrix && typeof parsed.matrix === 'object' ? parsed.matrix : null;
      if (!matrix) throw new Error('Formato invalido');
      await axios.put(`${API}/admin/allergens`, { matrix }, getAuthHeaders());
      toast.success('Matriz importada y guardada');
      fetchAllergenMatrix();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Archivo invalido o no compatible');
    }
  };

  // ---------- VeriFactu / Tickets ----------
  const fetchFiscalSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/fiscal/settings`, getAuthHeaders());
      setFiscal(data);
    } catch {}
  }, [getAuthHeaders]);

  const saveFiscalSettings = async () => {
    setFiscalSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, {
        fiscal_nif: fiscal.nif,
        fiscal_razon_social: fiscal.razon_social,
        fiscal_direccion: fiscal.direccion,
        fiscal_codigo_postal: fiscal.codigo_postal,
        fiscal_municipio: fiscal.municipio,
        fiscal_provincia: fiscal.provincia,
        fiscal_mode: fiscal.mode,
      }, getAuthHeaders());
      toast.success('Datos fiscales guardados');
      fetchFiscalSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setFiscalSaving(false);
    }
  };

  const fetchTickets = useCallback(async () => {
    try {
      const params = {};
      if (ticketsDateFrom) params.date_from = ticketsDateFrom;
      if (ticketsDateTo) params.date_to = ticketsDateTo;
      const { data } = await axios.get(`${API}/admin/tickets`, { ...getAuthHeaders(), params });
      setTickets(data.tickets || []);
      setTicketChain(data.chain || {});
    } catch {}
  }, [getAuthHeaders, ticketsDateFrom, ticketsDateTo]);

  const verifyChain = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/tickets/verify-chain`, getAuthHeaders());
      setChainStatus(data);
      if (data.ok) toast.success(`Cadena integra (${data.total} tickets)`);
      else toast.error(`Cadena rota en ${data.broken_at}`);
    } catch (err) {
      toast.error('Error al verificar');
    }
  };

  const exportOrdersCsv = async (kind, format = 'xlsx') => {
    let date_from, date_to;
    if (kind === 'month') {
      if (!exportMonth) { toast.error('Selecciona un mes'); return; }
      const [y, m] = exportMonth.split('-').map(Number);
      const last = new Date(y, m, 0).getDate(); // last day of month
      date_from = `${y}-${String(m).padStart(2, '0')}-01`;
      date_to = `${y}-${String(m).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
    } else {
      if (!exportFrom || !exportTo) { toast.error('Selecciona ambas fechas'); return; }
      date_from = exportFrom;
      date_to = exportTo;
    }
    try {
      const res = await axios.get(
        `${API}/admin/orders/export/${format}?date_from=${date_from}&date_to=${date_to}`,
        { ...getAuthHeaders(), responseType: 'blob' },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tajmahal-pedidos-${date_from}_${date_to}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} descargado — ya puedes enviarselo al gestor`);
    } catch (err) {
      toast.error('Error al exportar');
    }
  };

  const exportTicketsXml = async () => {    try {
      const params = new URLSearchParams();
      if (ticketsDateFrom) params.set('date_from', ticketsDateFrom);
      if (ticketsDateTo) params.set('date_to', ticketsDateTo);
      const url = `${API}/admin/tickets/export/xml${params.toString() ? '?' + params.toString() : ''}`;
      // Trigger download — auth via cookie
      const res = await axios.get(url, { ...getAuthHeaders(), responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `tajmahal-tickets-${stamp}.xml`;
      a.click();
      URL.revokeObjectURL(blobUrl);
      toast.success('XML descargado — guardado para inspeccion AEAT');
    } catch (err) {
      toast.error('Error al exportar');
    }
  };

  const viewTicketDetail = async (ticket) => {
    try {
      const { data } = await axios.get(`${API}/admin/tickets/${ticket.id}`, getAuthHeaders());
      setSelectedTicket(data);
    } catch {}
  };

  // ---------- Reviews admin ----------
  const [adminReviews, setAdminReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const fetchAdminReviews = useCallback(async () => {
    setReviewsLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/reviews`, getAuthHeaders());
      setAdminReviews(data);
    } catch (err) {
      toast.error('Error cargando reseñas');
    } finally {
      setReviewsLoading(false);
    }
  }, [getAuthHeaders]);

  const approveReview = async (id) => {
    try {
      await axios.post(`${API}/admin/reviews/${id}/approve`, {}, getAuthHeaders());
      toast.success('Reseña aprobada');
      fetchAdminReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };
  const deleteReview = async (id) => {
    if (!window.confirm('¿Eliminar esta reseña permanentemente?')) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, getAuthHeaders());
      toast.success('Reseña eliminada');
      fetchAdminReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  // ---------- Print Station mode ----------
  // When enabled on THIS device, this device auto-prints any accepted order
  // that hasn't been printed yet (regardless of which device accepted it).
  // Only enable on the device physically connected to the thermal printer.
  const [isPrintStation, setIsPrintStation] = useState(() => {
    try { return localStorage.getItem('taj_print_station') === '1'; } catch { return false; }
  });
  const togglePrintStation = () => {
    setIsPrintStation((prev) => {
      const next = !prev;
      try { localStorage.setItem('taj_print_station', next ? '1' : '0'); } catch {}
      toast.success(next
        ? 'Este dispositivo es la estacion de impresion'
        : 'Este dispositivo NO imprimira pedidos automaticamente');
      return next;
    });
  };

  // Auto-print loop: fires whenever orders are refreshed.
  // Guarded by a ref so we don't print the same order twice during a single session.
  const printingLockRef = useRef(new Set());
  useEffect(() => {
    if (!isLoggedIn || !isPrintStation || qzStatus !== 'ok') return;
    const pending = orders.filter(
      (o) => o.status === 'aceptado' && !o.printed && !printingLockRef.current.has(o.id),
    );
    if (pending.length === 0) return;
    pending.forEach(async (order) => {
      printingLockRef.current.add(order.id);
      try {
        // Fetch fiscal context (ticket + NIF + QR) so the receipt is
        // VeriFactu-compliant when printed.
        let fiscalContext = null;
        try {
          const { data } = await axios.get(`${API}/admin/print-data/${order.id}`, getAuthHeaders());
          fiscalContext = { ticket: data.ticket, fiscal: data.fiscal, qrBase64: data.qr_base64 };
        } catch {}
        await printOrderReceipt(order, { copies: 2, fiscalContext });
        await axios.post(`${API}/admin/orders/${order.id}/mark-printed`, {}, getAuthHeaders());
      } catch (err) {
        // Leave in the lock so we don't spam retries during this session; a page
        // refresh will try again. Log for visibility.
        toast.error('Error imprimiendo pedido #' + order.id.substring(0, 8));
        console.error('auto-print failed', order.id, err);
      }
    });
  }, [orders, isLoggedIn, isPrintStation, qzStatus, getAuthHeaders]);

  const fetchReport = useCallback(async (month) => {
    if (!token) return;
    setReportLoading(true);
    try {
      const response = await axios.get(`${API}/admin/reports?month=${month}`, getAuthHeaders());
      setReport(response.data);
    } catch {
      toast.error('Error al cargar informe');
    } finally {
      setReportLoading(false);
    }
  }, [token, getAuthHeaders]);

  const changeMonth = (direction) => {
    const [y, m] = reportMonth.split('-').map(Number);
    let newMonth, newYear;
    if (direction === 'next') {
      newMonth = m === 12 ? 1 : m + 1;
      newYear = m === 12 ? y + 1 : y;
    } else {
      newMonth = m === 1 ? 12 : m - 1;
      newYear = m === 1 ? y - 1 : y;
    }
    const newVal = `${newYear}-${String(newMonth).padStart(2, '0')}`;
    setReportMonth(newVal);
    fetchReport(newVal);
  };

  const monthName = (monthStr) => {
    const [y, m] = monthStr.split('-');
    const date = new Date(y, m - 1);
    return date.toLocaleDateString('es-ES', { year: 'numeric', month: 'long' });
  };

  const fetchDailyReport = async (date) => {
    try {
      const res = await axios.get(`${API}/admin/reports/daily?date=${date}`, getAuthHeaders());
      setDailyReport(res.data);
    } catch {
      toast.error('Error al cargar informe diario');
    }
  };

  const fetchRangeReport = async () => {
    if (!rangeFrom || !rangeTo) {
      toast.error('Selecciona ambas fechas');
      return;
    }
    if (rangeTo < rangeFrom) {
      toast.error('La fecha final no puede ser anterior a la inicial');
      return;
    }
    setRangeLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/reports/range?date_from=${rangeFrom}&date_to=${rangeTo}`,
        getAuthHeaders(),
      );
      setRangeReport(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cargar informe del rango');
    } finally {
      setRangeLoading(false);
    }
  };

  const prevPendingCount = useRef(0);
  const fetchPendingPayments = async (silent = false) => {
    if (!silent) setPendingLoading(true);
    try {
      const res = await axios.get(`${API}/admin/orders/pending-payment`, getAuthHeaders());
      const list = res.data || [];
      // Detect newly arrived pending payments and alert the admin (sound + toast)
      const newCount = list.length;
      if (newCount > prevPendingCount.current && prevPendingCount.current !== 0) {
        toast.info('Nuevo pago pendiente de confirmar en Redsys');
      } else if (newCount > 0 && prevPendingCount.current === 0 && silent) {
        // First background detection while admin is idle on another tab
        toast.info(`${newCount} pago(s) pendiente(s) en Redsys`);
      }
      prevPendingCount.current = newCount;
      setPendingPayments(list);
    } catch (e) {
      if (!silent) toast.error(e.response?.data?.detail || 'Error al cargar pagos pendientes');
    } finally {
      if (!silent) setPendingLoading(false);
    }
  };

  const recoverByRedsysRef = async () => {
    const ref = recoverRef.trim().toUpperCase();
    if (!/^[0-9]{4}[A-Z0-9]{8}$/.test(ref)) {
      toast.error('Formato de referencia Redsys invalido (12 caracteres: 4 digitos + 8 alfanumericos)');
      return;
    }
    if (!window.confirm(`Confirmar el pago del pedido con referencia ${ref}?\n\nSolo hazlo si has verificado que la transaccion esta "Autorizada" en el portal Redsys > Consultas > Operaciones.`)) return;
    try {
      const res = await axios.post(
        `${API}/admin/redsys/${ref}/confirm-payment`,
        {},
        getAuthHeaders(),
      );
      toast.success(res.data.message || 'Pago confirmado');
      setRecoverRef('');
      fetchPendingPayments();
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al confirmar el pago');
    }
  };

  const confirmPendingPayment = async (orderId) => {
    if (!window.confirm('Confirmar manualmente el pago de este pedido?\n\nSolo hazlo si has verificado el cargo en el portal Redsys.')) return;
    try {
      await axios.post(`${API}/admin/orders/${orderId}/confirm-payment`, {}, getAuthHeaders());
      toast.success('Pago confirmado. El pedido esta en la cola.');
      fetchPendingPayments();
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al confirmar el pago');
    }
  };

  const discardPendingPayment = async (orderId) => {
    if (!window.confirm('Descartar este pedido? Solo hazlo si estas seguro de que el cliente NO ha pagado.')) return;
    try {
      await axios.post(`${API}/admin/orders/${orderId}/discard-pending`, {}, getAuthHeaders());
      toast.success('Pedido descartado');
      fetchPendingPayments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al descartar el pedido');
    }
  };

  const refundRedsysOrder = async (order) => {
    const amount = Number(order.total || 0).toFixed(2);
    if (!window.confirm(`Reembolsar ${amount} EUR al cliente ${order.customer_name || ''}?\n\nEsta accion enviara la devolucion a Redsys y no se puede deshacer.`)) return;
    try {
      const res = await axios.post(`${API}/admin/orders/${order.id}/refund-redsys`, {}, getAuthHeaders());
      toast.success(res.data.message || 'Devolucion realizada');
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al procesar la devolucion');
    }
  };

  const printDailyReport = (data) => printDailySalesReport(data);

  const updateSetting = async (key, value) => {
    try {
      const response = await axios.put(`${API}/admin/settings`, { [key]: value }, getAuthHeaders());
      setSettings(response.data);
      toast.success('Ajuste actualizado');
    } catch {
      toast.error('Error al actualizar');
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, { status }, getAuthHeaders());
      fetchOrders();
      toast.success('Estado actualizado');
    } catch {
      toast.error('Error al actualizar');
    }
  };

  const acceptOrder = async (orderId) => {
    try {
      const res = await axios.post(`${API}/admin/orders/${orderId}/accept`, {}, getAuthHeaders());
      toast.success('Pedido aceptado — se imprimira en la estacion de impresion');
      // Route the confirmation through the pinned WhatsApp pane (reuses
      // the window if already open, opens it otherwise).
      const phone = res?.data?.customer_phone_wa;
      const msg = res?.data?.confirmation_message;
      if (phone && msg) sendToWhatsApp(phone, msg);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al aceptar');
    }
  };

  const sendConfirmation = async (orderId, template = 'status', delay = 10) => {
    try {
      const params = new URLSearchParams({ template, delay: String(delay) });
      const { data } = await axios.get(`${API}/admin/orders/${orderId}/confirmation?${params}`, getAuthHeaders());
      if (!data?.customer_phone_wa) {
        toast.error('No hay telefono valido del cliente');
        return;
      }
      sendToWhatsApp(data.customer_phone_wa, data.confirmation_message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al abrir WhatsApp');
    }
  };

  const cancelOrder = async (orderId) => {
    if (!window.confirm('¿Cancelar este pedido? Se procesara el reembolso automaticamente si fue pagado online.')) return;
    try {
      const res = await axios.post(`${API}/admin/orders/${orderId}/cancel`, {}, getAuthHeaders());
      fetchOrders();
      toast.success(res.data.refunded ? 'Pedido cancelado y reembolso procesado' : 'Pedido cancelado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cancelar');
    }
  };

  const getTimeSinceOrder = (createdAt) => {
    const elapsed = (Date.now() - new Date(createdAt).getTime()) / 1000;
    const mins = Math.floor(elapsed / 60);
    const secs = Math.floor(elapsed % 60);
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const printOrderWithFiscal = useCallback(async (order, opts = {}) => {
    let fiscalContext = null;
    try {
      const { data } = await axios.get(`${API}/admin/print-data/${order.id}`, getAuthHeaders());
      fiscalContext = { ticket: data.ticket, fiscal: data.fiscal, qrBase64: data.qr_base64 };
    } catch {}
    return printOrderReceipt(order, { ...opts, fiscalContext });
  }, [getAuthHeaders]);

  const printOrder = (order) => printOrderWithFiscal(order);

  const getStatusBadge = (status) => {
    const styles = {
      nuevo: 'bg-[#E28743] text-white',
      aceptado: 'bg-blue-500 text-white',
      confirmado: 'bg-blue-500 text-white',
      preparando: 'bg-[#C44536] text-white',
      listo: 'bg-[#2C5E3E] text-white',
      entregado: 'bg-gray-500 text-white',
      cancelado: 'bg-red-800 text-white'
    };
    return <Badge className={`${styles[status] || 'bg-gray-400 text-white'} capitalize`}>{status}</Badge>;
  };

  // LOGIN SCREEN
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-4" data-testid="admin-login">
        <div className="max-w-sm w-full bg-[#F5EFE6] rounded-2xl p-8 border border-[#E8DEC8]">
          <div className="text-center mb-6">
            <h1 className="font-serif text-2xl font-bold text-[#2D231E]">Panel Admin</h1>
            <p className="font-sans text-sm text-[#5C4D43]">Taj Mahal</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" type="email" className="bg-white" data-testid="admin-email" />
            <Input value={password} onChange={e => setPassword(e.target.value)} placeholder="Contrasena" type="password" className="bg-white" data-testid="admin-password" />
            <Button type="submit" className="btn-primary w-full py-3" data-testid="admin-login-btn">Iniciar Sesion</Button>
          </form>
        </div>
      </div>
    );
  }

  // ADMIN DASHBOARD
  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="admin-dashboard">
      {/* Hidden audio element for new-order tone (loops while 'nuevo' orders exist) */}
      <audio ref={notifAudioRef} src="/new-order-tone.mp3" preload="auto" data-testid="new-order-audio" />
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-[#2D231E]/10">
        <div className="container-max flex items-center justify-between h-16">
          <div className="flex items-center gap-2">
            <span className="font-serif text-xl font-bold text-[#C44536]">Admin</span>
            <span className="font-sans text-sm text-[#5C4D43]">Taj Mahal</span>
          </div>
          <div className="flex items-center gap-3">
            <span
              className={
                'hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ' +
                (qzStatus === 'ok'
                  ? 'bg-green-50 text-green-700 border-green-200'
                  : qzStatus === 'offline'
                    ? 'bg-gray-100 text-gray-600 border-gray-200'
                    : 'bg-amber-50 text-amber-700 border-amber-200')
              }
              title={
                qzStatus === 'ok'
                  ? 'QZ Tray conectado: impresion directa a impresora termica'
                  : qzStatus === 'offline'
                    ? 'QZ Tray no detectado: se usara dialogo del navegador. Instala QZ Tray desde qz.io/download/'
                    : 'Comprobando QZ Tray...'
              }
              data-testid="qz-status"
            >
              <Printer className="w-3 h-3" />
              {qzStatus === 'ok' ? 'Impresora OK' : qzStatus === 'offline' ? 'Sin QZ' : '...'}
            </span>
            <button
              onClick={togglePrintStation}
              className={
                'hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition ' +
                (isPrintStation
                  ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                  : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200')
              }
              title={
                isPrintStation
                  ? 'Este dispositivo esta configurado como estacion de impresion. Los pedidos aceptados desde cualquier dispositivo se imprimiran automaticamente aqui. Clic para desactivar.'
                  : 'Clic para marcar este dispositivo como la estacion de impresion (solo el dispositivo conectado a la impresora termica).'
              }
              data-testid="print-station-toggle"
            >
              {isPrintStation ? 'Estacion: ON' : 'Estacion: OFF'}
            </button>
            <Button variant="ghost" size="sm" onClick={fetchOrders} data-testid="refresh-orders">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              onClick={openWhatsAppPane}
              className="bg-[#25D366] hover:bg-[#1DA851] text-white whitespace-nowrap"
              data-testid="open-whatsapp-pane"
              title="Abre WhatsApp Web en un panel fijado a la derecha"
            >
              <MessageCircle className="w-4 h-4 mr-1.5" /> WhatsApp Web
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="admin-logout">
              <LogOut className="w-4 h-4 mr-1" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <div className="container-max py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 bg-[#F5EFE6]">
            <TabsTrigger value="orders" className="gap-2" data-testid="tab-orders">
              <ShoppingBag className="w-4 h-4" /> Pedidos
            </TabsTrigger>
            <TabsTrigger value="menu-mgmt" className="gap-2" data-testid="tab-menu-mgmt" onClick={() => { if (menuItems.length === 0) fetchMenu(); }}>
              <UtensilsCrossed className="w-4 h-4" /> Menu
            </TabsTrigger>
            <TabsTrigger value="reports" className="gap-2" data-testid="tab-reports" onClick={() => { if (!report) fetchReport(reportMonth); }}>
              <BarChart3 className="w-4 h-4" /> Informes
            </TabsTrigger>
            <TabsTrigger value="reviews" className="gap-2" data-testid="tab-reviews" onClick={() => fetchAdminReviews()}>
              <Star className="w-4 h-4" /> Reseñas
            </TabsTrigger>
            <TabsTrigger value="discounts" className="gap-2" data-testid="tab-discounts" onClick={() => fetchDiscountCodes()}>
              <Tag className="w-4 h-4" /> Descuentos
            </TabsTrigger>
            <TabsTrigger value="allergens" className="gap-2" data-testid="tab-allergens" onClick={() => fetchAllergenMatrix()}>
              <AlertTriangle className="w-4 h-4" /> Alergenos
            </TabsTrigger>
            <TabsTrigger value="tickets" className="gap-2" data-testid="tab-tickets" onClick={() => { fetchTickets(); fetchFiscalSettings(); }}>
              <FileText className="w-4 h-4" /> Tickets
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-2" data-testid="tab-settings" onClick={() => fetchFiscalSettings()}>
              <Settings className="w-4 h-4" /> Ajustes
            </TabsTrigger>
            <TabsTrigger value="payments" className="gap-2 relative" data-testid="tab-payments" onClick={() => fetchPendingPayments()}>
              <CreditCard className="w-4 h-4" /> Pagos pendientes
              {pendingPayments.length > 0 && (
                <span
                  className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#C44536] text-white text-[11px] font-semibold animate-pulse"
                  data-testid="pending-payments-badge"
                >
                  {pendingPayments.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* ORDERS TAB */}
          <TabsContent value="orders">
            <div className="space-y-4">
              {/* Business-day header + date picker + export trigger */}
              <div className="bg-white rounded-xl px-4 py-3 border border-[#E8DEC8] space-y-3" data-testid="orders-day-header">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-[#2D231E]">
                    <Clock className="w-4 h-4 text-[#C44536]" />
                    <span className="font-sans text-sm">
                      {ordersDate ? 'Pedidos del' : 'Pedidos de hoy'}
                      {(ordersDate || businessDate) && (
                        <strong className="ml-1" data-testid="orders-business-date">
                          {new Date((ordersDate || businessDate) + 'T12:00:00').toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}
                        </strong>
                      )}
                      <span className="text-[#8A7A6D] ml-2 text-xs">(corte 02:00)</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Input
                      type="date"
                      value={ordersDate || businessDate || ''}
                      max={businessDate || undefined}
                      onChange={(e) => setOrdersDate(e.target.value)}
                      className="w-[170px] bg-white border-[#E8DEC8] text-sm"
                      data-testid="orders-date-picker"
                    />
                    {ordersDate && (
                      <Button size="sm" variant="outline" onClick={() => setOrdersDate('')} data-testid="orders-date-today">
                        Hoy
                      </Button>
                    )}
                    <Button
                      size="sm"
                      onClick={() => setShowExport(s => !s)}
                      data-testid="toggle-export-panel"
                      className="bg-[#2C5E3E] hover:bg-[#1D4A2E] text-white whitespace-nowrap"
                    >
                      <Download className="w-4 h-4 mr-1.5" /> Exportar para gestor
                    </Button>
                  </div>
                </div>
              </div>

              {/* Export panel — month / custom range CSV */}
              {showExport && (
                <div className="bg-[#F5EFE6] rounded-xl px-4 py-3 border border-[#E8DEC8]" data-testid="export-panel">
                  <p className="font-serif text-sm font-semibold text-[#2D231E] mb-2">Exportar pedidos para tu gestor (CSV)</p>
                  <div className="flex flex-wrap gap-2 items-end">
                    <div>
                      <label className="text-xs text-[#5C4D43] block mb-1">Mes</label>
                      <Input
                        type="month"
                        value={exportMonth}
                        onChange={(e) => setExportMonth(e.target.value)}
                        className="bg-white border-[#E8DEC8]"
                        data-testid="export-month-picker"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => exportOrdersCsv('month', 'xlsx')} className="btn-primary" data-testid="export-month-btn">
                        <Download className="w-4 h-4 mr-1" /> Descargar mes (XLSX)
                      </Button>
                      <Button onClick={() => exportOrdersCsv('month', 'csv')} variant="outline" size="sm" data-testid="export-month-csv-btn" title="Alternativa CSV">
                        CSV
                      </Button>
                    </div>
                    <span className="text-[#8A7A6D] text-xs px-2">o</span>
                    <div>
                      <label className="text-xs text-[#5C4D43] block mb-1">Desde</label>
                      <Input type="date" value={exportFrom} onChange={(e) => setExportFrom(e.target.value)} className="bg-white border-[#E8DEC8]" data-testid="export-date-from" />
                    </div>
                    <div>
                      <label className="text-xs text-[#5C4D43] block mb-1">Hasta</label>
                      <Input type="date" value={exportTo} onChange={(e) => setExportTo(e.target.value)} className="bg-white border-[#E8DEC8]" data-testid="export-date-to" />
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => exportOrdersCsv('range', 'xlsx')} disabled={!exportFrom || !exportTo} data-testid="export-range-btn">
                        <Download className="w-4 h-4 mr-1" /> Descargar rango (XLSX)
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => exportOrdersCsv('range', 'csv')} disabled={!exportFrom || !exportTo} data-testid="export-range-csv-btn" title="Alternativa CSV">
                        CSV
                      </Button>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-[#8A7A6D]">
                    El XLSX (Excel) incluye fecha, total, base IVA, cuota IVA 10%, descuento, envío, método de pago y artículos, con formato listo para el gestor. Excluye cancelados. También disponible en CSV clásico.
                  </p>
                </div>
              )}

              {orders.length === 0 ? (
                <div className="text-center py-12 bg-[#F5EFE6] rounded-2xl">
                  <ShoppingBag className="w-12 h-12 text-[#E8DEC8] mx-auto mb-3" />
                  <p className="font-sans text-[#5C4D43]">
                    {ordersDate ? 'No hay pedidos en esa fecha' : 'No hay pedidos todavia hoy'}
                  </p>
                </div>
              ) : (
                orders.map(order => (
                  <div key={order.id} className={`rounded-2xl p-5 border ${order.status === 'nuevo' ? 'bg-[#C44536]/5 border-[#C44536]/30 ring-2 ring-[#C44536]/20' : order.status === 'cancelado' ? 'bg-gray-100 border-gray-300 opacity-70' : 'bg-[#F5EFE6] border-[#E8DEC8]'}`} data-testid={`order-${order.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                      <div className="flex items-center gap-3">
                        <span className="font-sans text-xs text-[#5C4D43]">#{order.id.substring(0, 8)}</span>
                        {getStatusBadge(order.status)}
                        <Badge variant="outline" className="capitalize">{order.order_type === 'pickup' ? 'Recogida' : 'Entrega'}</Badge>
                        <Badge variant="outline">{order.payment_method === 'whatsapp' ? 'WhatsApp' : 'Online'}</Badge>
                        {order.payment_method === 'whatsapp' && (
                          <Badge className="bg-amber-500 text-white">
                            {order.payment_mode === 'cash' ? 'Efectivo' : 'Tarjeta'} - PENDIENTE
                          </Badge>
                        )}
                        {order.payment_method === 'online' && order.payment_status === 'pagado' && (
                          <Badge className="bg-[#2C5E3E] text-white">Pagado</Badge>
                        )}
                        {order.status === 'nuevo' && (
                          <span className="font-sans text-xs text-[#C44536] font-bold animate-pulse">
                            NUEVO - Aceptar antes de 5 min
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Accept button for new orders */}
                        {order.status === 'nuevo' && (
                          <>
                            <Button size="sm" onClick={() => acceptOrder(order.id)} className="bg-[#2C5E3E] hover:bg-[#234d32] text-white" data-testid={`accept-order-${order.id}`}>
                              <CheckCircle className="w-4 h-4 mr-1" /> Aceptar
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => cancelOrder(order.id)} data-testid={`cancel-order-${order.id}`}>
                              <XCircle className="w-4 h-4 mr-1" /> Rechazar
                            </Button>
                          </>
                        )}
                        {order.status !== 'nuevo' && order.status !== 'cancelado' && order.status !== 'entregado' && (
                          <Button size="sm" variant="destructive" onClick={() => cancelOrder(order.id)} data-testid={`cancel-order-${order.id}`}>
                            <XCircle className="w-4 h-4 mr-1" /> Cancelar
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => setSelectedOrder(selectedOrder === order.id ? null : order.id)} data-testid={`view-order-${order.id}`}>
                          <Eye className="w-4 h-4 mr-1" /> Ver
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => printOrder(order)} data-testid={`print-order-${order.id}`}>
                          <Printer className="w-4 h-4 mr-1" /> Imprimir
                        </Button>
                        {order.payment_provider === 'redsys' && order.payment_status === 'pagado' && order.refund_status !== 'refunded' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-orange-500 text-orange-600 hover:bg-orange-50"
                            onClick={() => refundRedsysOrder(order)}
                            data-testid={`refund-order-${order.id}`}
                          >
                            <RotateCcw className="w-4 h-4 mr-1" /> Reembolsar
                          </Button>
                        )}
                        {order.refund_status === 'refunded' && (
                          <span className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded" data-testid={`refunded-badge-${order.id}`}>
                            Reembolsado
                          </span>
                        )}
                        {order.status !== 'cancelado' && order.customer_phone && (
                          <div className="relative inline-block" data-testid={`whatsapp-group-${order.id}`}>
                            <div className="flex">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => sendConfirmation(order.id, 'status')}
                                className="border-[#25D366] text-[#1DA851] hover:bg-[#25D366]/10 rounded-r-none border-r-0"
                                data-testid={`whatsapp-confirm-${order.id}`}
                                title="Enviar mensaje segun estado actual"
                              >
                                <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setWaMenuFor(waMenuFor === order.id ? null : order.id)}
                                className="border-[#25D366] text-[#1DA851] hover:bg-[#25D366]/10 rounded-l-none px-2"
                                data-testid={`whatsapp-menu-${order.id}`}
                                title="Mas plantillas"
                              >
                                ▾
                              </Button>
                            </div>
                            {waMenuFor === order.id && (
                              <div className="absolute right-0 top-full mt-1 bg-white border border-[#E8DEC8] rounded-lg shadow-lg z-30 min-w-[200px] py-1 text-sm" data-testid={`wa-templates-${order.id}`}>
                                {[
                                  { key: 'accepted',         label: 'Confirmado' },
                                  { key: 'ready',            label: 'Listo' },
                                  { key: 'on_the_way',       label: 'En camino' },
                                  { key: 'delay',            label: 'Retraso 10 min' },
                                  { key: 'item_unavailable', label: 'Articulo agotado' },
                                  { key: 'thanks',           label: 'Gracias (resena)' },
                                ].map(tpl => (
                                  <button
                                    key={tpl.key}
                                    type="button"
                                    onClick={() => { sendConfirmation(order.id, tpl.key, 10); setWaMenuFor(null); }}
                                    className="block w-full text-left px-3 py-2 hover:bg-[#F5EFE6] text-[#2D231E]"
                                    data-testid={`wa-tpl-${tpl.key}-${order.id}`}
                                  >
                                    {tpl.label}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-4 text-sm">
                      <span className="font-sans"><strong>Cliente:</strong> {order.customer_name}</span>
                      <span className="font-sans"><strong>Tel:</strong> {order.customer_phone}</span>
                      <span className="font-sans font-bold text-[#C44536]">{order.total?.toFixed(2)} EUR</span>
                      <span className="font-sans text-[#5C4D43]">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {new Date(order.created_at).toLocaleString('es-ES')}
                      </span>
                      {order.cancel_reason && (
                        <span className="font-sans text-red-600 text-xs">Motivo: {order.cancel_reason}</span>
                      )}
                    </div>

                    {/* Expanded order details */}
                    {selectedOrder === order.id && (
                      <div className="mt-4 pt-4 border-t border-[#E8DEC8]">
                        {order.customer_email && <p className="font-sans text-sm mb-2"><strong>Email:</strong> {order.customer_email}</p>}
                        {order.customer_address && <p className="font-sans text-sm mb-2"><strong>Direccion:</strong> {order.customer_address}</p>}
                        {order.notes && <p className="font-sans text-sm mb-2"><strong>Notas:</strong> {order.notes}</p>}
                        <div className="space-y-1 mb-4">
                          {order.items?.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-sm font-sans">
                              <span>{item.name}{item.spiciness ? ` (${item.spiciness})` : ''} x{item.quantity}</span>
                              <span>{(item.price * item.quantity).toFixed(2)} EUR</span>
                            </div>
                          ))}
                        </div>
                        <Separator className="my-3 bg-[#E8DEC8]" />
                        {order.status !== 'cancelado' && order.status !== 'nuevo' && (
                          <div className="flex flex-wrap gap-2">
                            {['aceptado', 'preparando', 'listo', 'entregado'].map(status => (
                              <Button
                                key={status}
                                size="sm"
                                variant={order.status === status ? 'default' : 'outline'}
                                onClick={() => updateOrderStatus(order.id, status)}
                                className={`capitalize text-xs ${order.status === status ? 'bg-[#C44536] text-white' : ''}`}
                                data-testid={`status-${status}-${order.id}`}
                              >
                                {status}
                              </Button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* MENU MANAGEMENT TAB */}
          <TabsContent value="menu-mgmt">
            <div className="space-y-4">
              {/* Search and filter */}
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C4D43]" />
                  <Input
                    value={menuSearch}
                    onChange={e => setMenuSearch(e.target.value)}
                    placeholder="Buscar plato..."
                    className="pl-10 bg-[#F5EFE6] border-[#E8DEC8]"
                    data-testid="menu-search"
                  />
                </div>
                <select
                  value={menuCategory}
                  onChange={e => setMenuCategory(e.target.value)}
                  className="px-4 py-2 rounded-lg bg-[#F5EFE6] border border-[#E8DEC8] text-sm text-[#2D231E]"
                  data-testid="menu-category-filter"
                >
                  <option value="Todos">Todas las categorias</option>
                  {[...new Set(menuItems.map(i => i.category))].sort().map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div className="text-sm text-[#5C4D43] flex gap-4">
                <span>Total: {menuItems.length}</span>
                <span className="text-red-600">No disponibles: {frozenIds.length}</span>
              </div>

              {/* Menu items list */}
              <div className="space-y-2">
                {menuItems
                  .filter(item => {
                    const matchSearch = !menuSearch || item.name.toLowerCase().includes(menuSearch.toLowerCase());
                    const matchCat = menuCategory === 'Todos' || item.category === menuCategory;
                    return matchSearch && matchCat;
                  })
                  .map(item => {
                    const isFrozen = frozenIds.includes(item.id);
                    return (
                      <div
                        key={item.id}
                        className={`flex items-center justify-between p-3 rounded-xl border ${isFrozen ? 'bg-red-50 border-red-200 opacity-70' : 'bg-[#F5EFE6] border-[#E8DEC8]'}`}
                        data-testid={`menu-mgmt-${item.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`font-sans text-sm font-medium ${isFrozen ? 'text-red-600 line-through' : 'text-[#2D231E]'}`}>
                              {item.name}
                            </span>
                            <Badge variant="outline" className="text-xs">{item.category}</Badge>
                            {isFrozen && <Badge className="bg-red-600 text-white text-xs">No disponible</Badge>}
                          </div>
                          <span className="font-sans text-xs text-[#5C4D43]">{item.price.toFixed(2)} EUR</span>
                        </div>
                        <Switch
                          checked={!isFrozen}
                          onCheckedChange={() => toggleMenuItem(item.id)}
                          data-testid={`toggle-menu-${item.id}`}
                        />
                      </div>
                    );
                  })}
              </div>
            </div>
          </TabsContent>

          {/* REPORTS TAB */}
          <TabsContent value="reports">
            <div className="space-y-6">
              {/* DAILY SALES REPORT */}
              <div className="bg-[#F5EFE6] rounded-2xl p-6 border border-[#E8DEC8]">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                  <h2 className="font-serif text-lg font-semibold text-[#2D231E]">Ventas del Dia</h2>
                  <div className="flex items-center gap-3">
                    <Input
                      type="date"
                      value={dailyDate}
                      onChange={e => { setDailyDate(e.target.value); fetchDailyReport(e.target.value); }}
                      className="bg-white border-[#E8DEC8] w-44"
                      data-testid="daily-date-picker"
                    />
                    <Button size="sm" onClick={() => fetchDailyReport(dailyDate)} className="bg-[#C44536] text-white hover:bg-[#A83A2D]" data-testid="daily-report-load">
                      <RefreshCw className="w-4 h-4 mr-1" /> Cargar
                    </Button>
                    {dailyReport && (
                      <Button size="sm" variant="outline" onClick={() => printDailyReport(dailyReport)} data-testid="daily-report-print">
                        <Printer className="w-4 h-4 mr-1" /> Imprimir
                      </Button>
                    )}
                  </div>
                </div>

                {dailyReport ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-white rounded-xl p-4 text-center">
                      <p className="font-sans text-xs text-[#5C4D43]">Total Pedidos</p>
                      <p className="font-serif text-2xl font-bold text-[#2D231E]">{dailyReport.total_orders}</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 text-center">
                      <p className="font-sans text-xs text-[#5C4D43]">Ingresos Totales</p>
                      <p className="font-serif text-2xl font-bold text-[#C44536]">{dailyReport.total_revenue.toFixed(2)} EUR</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 text-center">
                      <p className="font-sans text-xs text-[#5C4D43]">Web / WhatsApp</p>
                      <p className="font-sans text-sm font-medium">{dailyReport.online_count} / {dailyReport.whatsapp_count}</p>
                      <p className="font-sans text-xs text-[#5C4D43]">{dailyReport.online_revenue.toFixed(2)} / {dailyReport.whatsapp_revenue.toFixed(2)}</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 text-center">
                      <p className="font-sans text-xs text-[#5C4D43]">Tarjeta / Efectivo</p>
                      <p className="font-sans text-sm font-medium">{dailyReport.card_count} / {dailyReport.cash_count}</p>
                      <p className="font-sans text-xs text-[#5C4D43]">{dailyReport.card_revenue.toFixed(2)} / {dailyReport.cash_revenue.toFixed(2)}</p>
                    </div>
                  </div>
                ) : (
                  <p className="font-sans text-sm text-[#5C4D43] text-center py-4">Selecciona una fecha y haz clic en "Cargar"</p>
                )}
              </div>

              <Separator className="bg-[#E8DEC8]" />

              {/* CUSTOM RANGE REPORT - until VeriFactu is fully active,
                  let the admin pull a sales summary for any arbitrary
                  date range (same shape as the monthly report). */}
              <div className="bg-[#F5EFE6] rounded-2xl p-4 border border-[#E8DEC8] space-y-4">
                <h3 className="font-serif text-lg font-semibold text-[#2D231E]">
                  Informe por rango de fechas
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                  <div>
                    <label className="font-sans text-xs text-[#5C4D43] block mb-1">Desde</label>
                    <Input
                      type="date"
                      value={rangeFrom}
                      onChange={(e) => setRangeFrom(e.target.value)}
                      max={rangeTo || undefined}
                      className="bg-white"
                      data-testid="range-from-input"
                    />
                  </div>
                  <div>
                    <label className="font-sans text-xs text-[#5C4D43] block mb-1">Hasta</label>
                    <Input
                      type="date"
                      value={rangeTo}
                      onChange={(e) => setRangeTo(e.target.value)}
                      min={rangeFrom || undefined}
                      max={new Date().toISOString().split('T')[0]}
                      className="bg-white"
                      data-testid="range-to-input"
                    />
                  </div>
                  <Button
                    onClick={fetchRangeReport}
                    disabled={rangeLoading}
                    className="bg-[#C44536] hover:bg-[#A8392C] text-white"
                    data-testid="range-generate-btn"
                  >
                    {rangeLoading ? (
                      <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <BarChart3 className="w-4 h-4 mr-1" />
                    )}
                    Generar informe
                  </Button>
                </div>

                {rangeReport && (
                  <div className="bg-white rounded-xl p-4 border border-[#E8DEC8]">
                    <p className="font-sans text-sm text-[#5C4D43] mb-3" data-testid="range-label">
                      {rangeReport.date_from} → {rangeReport.date_to}
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="text-center">
                        <p className="font-sans text-xs text-[#5C4D43]">Pedidos</p>
                        <p className="font-serif text-2xl font-bold text-[#2D231E]" data-testid="range-total-orders">
                          {rangeReport.total_orders}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="font-sans text-xs text-[#5C4D43]">Ingresos</p>
                        <p className="font-serif text-2xl font-bold text-[#C44536]" data-testid="range-total-revenue">
                          {rangeReport.total_revenue.toFixed(2)} EUR
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="font-sans text-xs text-[#5C4D43]">Entrega / Recogida</p>
                        <p className="font-serif text-lg font-bold text-[#2D231E]">
                          {rangeReport.by_type.delivery.count} / {rangeReport.by_type.pickup.count}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="font-sans text-xs text-[#5C4D43]">Online / WhatsApp</p>
                        <p className="font-serif text-lg font-bold text-[#2D231E]">
                          {rangeReport.by_payment.online.count} / {rangeReport.by_payment.whatsapp.count}
                        </p>
                      </div>
                    </div>

                    {rangeReport.daily_breakdown.length > 0 && (
                      <div className="overflow-x-auto mt-4 max-h-72">
                        <table className="w-full text-sm">
                          <thead className="sticky top-0 bg-white">
                            <tr className="border-b border-[#E8DEC8]">
                              <th className="text-left font-sans font-semibold text-[#2D231E] py-2">Fecha</th>
                              <th className="text-right font-sans font-semibold text-[#2D231E] py-2">Pedidos</th>
                              <th className="text-right font-sans font-semibold text-[#2D231E] py-2">Ingresos</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rangeReport.daily_breakdown.map((day, idx) => (
                              <tr key={idx} className="border-b border-[#E8DEC8]/50">
                                <td className="font-sans text-[#2D231E] py-1.5">
                                  {new Date(day.date).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })}
                                </td>
                                <td className="text-right font-sans text-[#2D231E] py-1.5">{day.orders}</td>
                                <td className="text-right font-sans font-medium text-[#C44536] py-1.5">{day.revenue.toFixed(2)} EUR</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(
                          `${API}/admin/orders/export/xlsx?date_from=${rangeReport.date_from}&date_to=${rangeReport.date_to}`,
                          '_blank',
                        )}
                        data-testid="range-xlsx-btn"
                      >
                        <Download className="w-4 h-4 mr-1" /> Descargar XLSX
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(
                          `${API}/admin/orders/export/csv?date_from=${rangeReport.date_from}&date_to=${rangeReport.date_to}`,
                          '_blank',
                        )}
                        data-testid="range-csv-btn"
                      >
                        CSV
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <Separator className="bg-[#E8DEC8]" />

              {/* MONTHLY REPORT - Month Selector */}
              <div className="flex items-center justify-between bg-[#F5EFE6] rounded-2xl p-4 border border-[#E8DEC8]">
                <Button variant="ghost" size="sm" onClick={() => changeMonth('prev')} data-testid="report-prev-month">
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <h2 className="font-serif text-xl font-semibold text-[#2D231E] capitalize" data-testid="report-month-label">
                  {monthName(reportMonth)}
                </h2>
                <Button variant="ghost" size="sm" onClick={() => changeMonth('next')} data-testid="report-next-month">
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>

              {reportLoading ? (
                <div className="text-center py-12"><RefreshCw className="w-8 h-8 text-[#C44536] animate-spin mx-auto" /></div>
              ) : report ? (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-[#F5EFE6] rounded-2xl p-5 border border-[#E8DEC8] text-center">
                      <p className="font-sans text-sm text-[#5C4D43]">Total Pedidos</p>
                      <p className="font-serif text-3xl font-bold text-[#2D231E]" data-testid="report-total-orders">{report.total_orders}</p>
                    </div>
                    <div className="bg-[#F5EFE6] rounded-2xl p-5 border border-[#E8DEC8] text-center">
                      <p className="font-sans text-sm text-[#5C4D43]">Ingresos Totales</p>
                      <p className="font-serif text-3xl font-bold text-[#C44536]" data-testid="report-total-revenue">{report.total_revenue.toFixed(2)} EUR</p>
                    </div>
                    <div className="bg-[#F5EFE6] rounded-2xl p-5 border border-[#E8DEC8] text-center">
                      <p className="font-sans text-sm text-[#5C4D43]">Entrega</p>
                      <p className="font-serif text-2xl font-bold text-[#2D231E]">{report.by_type.delivery.count}</p>
                      <p className="font-sans text-xs text-[#5C4D43]">{report.by_type.delivery.revenue.toFixed(2)} EUR</p>
                    </div>
                    <div className="bg-[#F5EFE6] rounded-2xl p-5 border border-[#E8DEC8] text-center">
                      <p className="font-sans text-sm text-[#5C4D43]">Recogida</p>
                      <p className="font-serif text-2xl font-bold text-[#2D231E]">{report.by_type.pickup.count}</p>
                      <p className="font-sans text-xs text-[#5C4D43]">{report.by_type.pickup.revenue.toFixed(2)} EUR</p>
                    </div>
                  </div>

                  {/* Payment Method Breakdown */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-[#F5EFE6] rounded-2xl p-5 border border-[#E8DEC8]">
                      <h3 className="font-serif text-lg font-semibold text-[#2D231E] mb-4">Por Metodo de Pago</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-[#C44536]"></div>
                            <span className="font-sans text-sm">Online (Stripe)</span>
                          </div>
                          <div className="text-right">
                            <span className="font-sans text-sm font-medium">{report.by_payment.online.count} pedidos</span>
                            <span className="font-sans text-xs text-[#5C4D43] ml-2">{report.by_payment.online.revenue.toFixed(2)} EUR</span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-[#25D366]"></div>
                            <span className="font-sans text-sm">WhatsApp</span>
                          </div>
                          <div className="text-right">
                            <span className="font-sans text-sm font-medium">{report.by_payment.whatsapp.count} pedidos</span>
                            <span className="font-sans text-xs text-[#5C4D43] ml-2">{report.by_payment.whatsapp.revenue.toFixed(2)} EUR</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Top Dishes */}
                    <div className="bg-[#F5EFE6] rounded-2xl p-5 border border-[#E8DEC8]">
                      <h3 className="font-serif text-lg font-semibold text-[#2D231E] mb-4">Platos Mas Vendidos</h3>
                      <div className="space-y-2">
                        {report.top_dishes.length === 0 ? (
                          <p className="font-sans text-sm text-[#5C4D43]">Sin datos</p>
                        ) : (
                          report.top_dishes.map((dish, idx) => (
                            <div key={idx} className="flex justify-between items-center">
                              <span className="font-sans text-sm text-[#2D231E] truncate flex-1">{idx + 1}. {dish.name}</span>
                              <Badge className="bg-[#C44536] text-white ml-2">{dish.quantity}</Badge>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Daily Breakdown */}
                  <div className="bg-[#F5EFE6] rounded-2xl p-5 border border-[#E8DEC8]">
                    <h3 className="font-serif text-lg font-semibold text-[#2D231E] mb-4">Desglose Diario</h3>
                    {report.daily_breakdown.length === 0 ? (
                      <p className="font-sans text-sm text-[#5C4D43]">Sin datos para este mes</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-[#E8DEC8]">
                              <th className="text-left font-sans font-semibold text-[#2D231E] py-2">Fecha</th>
                              <th className="text-right font-sans font-semibold text-[#2D231E] py-2">Pedidos</th>
                              <th className="text-right font-sans font-semibold text-[#2D231E] py-2">Ingresos</th>
                            </tr>
                          </thead>
                          <tbody>
                            {report.daily_breakdown.map((day, idx) => (
                              <tr key={idx} className="border-b border-[#E8DEC8]/50">
                                <td className="font-sans text-[#2D231E] py-2">
                                  {new Date(day.date).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })}
                                </td>
                                <td className="text-right font-sans text-[#2D231E] py-2">{day.orders}</td>
                                <td className="text-right font-sans font-medium text-[#C44536] py-2">{day.revenue.toFixed(2)} EUR</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="border-t-2 border-[#E8DEC8]">
                              <td className="font-sans font-bold text-[#2D231E] py-2">Total</td>
                              <td className="text-right font-sans font-bold text-[#2D231E] py-2">{report.total_orders}</td>
                              <td className="text-right font-sans font-bold text-[#C44536] py-2">{report.total_revenue.toFixed(2)} EUR</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-12 bg-[#F5EFE6] rounded-2xl">
                  <BarChart3 className="w-12 h-12 text-[#E8DEC8] mx-auto mb-3" />
                  <p className="font-sans text-[#5C4D43]">Haz clic en "Informes" para ver el informe mensual</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* REVIEWS TAB */}
          <TabsContent value="reviews">
            <div className="max-w-5xl">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-xl font-semibold text-[#2D231E]">Reseñas de Clientes</h2>
                <Button variant="outline" size="sm" onClick={fetchAdminReviews} data-testid="refresh-reviews">
                  <RefreshCw className={`w-4 h-4 mr-1 ${reviewsLoading ? 'animate-spin' : ''}`} /> Actualizar
                </Button>
              </div>
              {adminReviews.length === 0 && !reviewsLoading && (
                <div className="bg-[#F5EFE6] rounded-2xl p-8 text-center border border-[#E8DEC8]">
                  <p className="text-[#5C4D43]">Aun no hay reseñas.</p>
                </div>
              )}
              <div className="space-y-3">
                {adminReviews.map((r) => (
                  <div
                    key={r.id}
                    className={`bg-white rounded-xl p-4 border flex flex-col sm:flex-row sm:items-start gap-4 ${r.approved ? 'border-[#E8DEC8]' : 'border-amber-300 bg-amber-50/50'}`}
                    data-testid={`admin-review-${r.id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-sans font-semibold text-[#2D231E]">{r.name}</span>
                        <span className="flex gap-0.5">
                          {Array.from({ length: 5 }, (_, i) => (
                            <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? 'fill-[#E28743] text-[#E28743]' : 'text-[#E8DEC8]'}`} />
                          ))}
                        </span>
                        {r.approved
                          ? <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Publicada</span>
                          : <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">Pendiente</span>
                        }
                        <span className="text-xs text-[#8A7A6D]">
                          {new Date(r.created_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}
                        </span>
                      </div>
                      {r.comment && <p className="font-sans text-sm text-[#5C4D43] italic">&ldquo;{r.comment}&rdquo;</p>}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {!r.approved && (
                        <Button
                          size="sm"
                          className="bg-[#2C5E3E] hover:bg-[#234d32] text-white"
                          onClick={() => approveReview(r.id)}
                          data-testid={`approve-review-${r.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" /> Aprobar
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-300 text-red-700 hover:bg-red-50"
                        onClick={() => deleteReview(r.id)}
                        data-testid={`delete-review-${r.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* DISCOUNTS TAB */}
          <TabsContent value="discounts">
            <div className="max-w-4xl space-y-6">
              <div className="bg-white rounded-2xl p-6 border border-[#E8DEC8]">
                <h2 className="font-serif text-xl font-semibold text-[#2D231E] mb-4">Crear codigo de descuento</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="font-sans text-sm text-[#5C4D43] block mb-1">Codigo *</label>
                    <Input
                      value={discountForm.code}
                      onChange={(e) => setDiscountForm({ ...discountForm, code: e.target.value.toUpperCase() })}
                      placeholder="BIENVENIDA5"
                      className="bg-[#FDFBF7] uppercase"
                      maxLength={30}
                      data-testid="discount-new-code"
                    />
                  </div>
                  <div>
                    <label className="font-sans text-sm text-[#5C4D43] block mb-1">Porcentaje (%) *</label>
                    <Input
                      type="number" min="1" max="100" step="1"
                      value={discountForm.percentage}
                      onChange={(e) => setDiscountForm({ ...discountForm, percentage: parseFloat(e.target.value) || 0 })}
                      className="bg-[#FDFBF7]"
                      data-testid="discount-new-percentage"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="font-sans text-sm text-[#5C4D43] block mb-1">Descripcion (opcional)</label>
                    <Input
                      value={discountForm.description}
                      onChange={(e) => setDiscountForm({ ...discountForm, description: e.target.value })}
                      placeholder="5% de descuento de bienvenida"
                      className="bg-[#FDFBF7]"
                      maxLength={200}
                    />
                  </div>
                  <div>
                    <label className="font-sans text-sm text-[#5C4D43] block mb-1">Tipo</label>
                    <select
                      value={discountForm.code_type}
                      onChange={(e) => setDiscountForm({ ...discountForm, code_type: e.target.value })}
                      className="w-full bg-[#FDFBF7] border border-[#E8DEC8] rounded-md px-3 py-2 text-sm"
                      data-testid="discount-new-type"
                    >
                      <option value="new_customer">Solo nuevos clientes (tel. + email no usados)</option>
                      <option value="generic">Cualquier cliente (un uso por cliente tambien)</option>
                    </select>
                  </div>
                  <div>
                    <label className="font-sans text-sm text-[#5C4D43] block mb-1">Pedido minimo (EUR)</label>
                    <Input
                      type="number" min="0" step="0.5"
                      value={discountForm.min_order_amount}
                      onChange={(e) => setDiscountForm({ ...discountForm, min_order_amount: parseFloat(e.target.value) || 0 })}
                      className="bg-[#FDFBF7]"
                    />
                  </div>
                </div>
                <Button onClick={createDiscountCode} className="btn-primary mt-4" data-testid="discount-create-btn">
                  <Plus className="w-4 h-4 mr-1" /> Crear codigo
                </Button>
              </div>

              <div>
                <h3 className="font-serif text-lg font-semibold text-[#2D231E] mb-3">Codigos existentes</h3>
                {discountCodes.length === 0 ? (
                  <div className="bg-[#F5EFE6] rounded-2xl p-6 text-center text-[#5C4D43]">Aun no hay codigos creados.</div>
                ) : (
                  <div className="space-y-2">
                    {discountCodes.map((d) => (
                      <div
                        key={d.id}
                        className={`bg-white rounded-xl p-4 border flex flex-col sm:flex-row sm:items-center gap-3 ${d.active ? 'border-[#E8DEC8]' : 'border-gray-200 opacity-60'}`}
                        data-testid={`discount-row-${d.id}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <code className="font-mono font-bold text-[#C44536] text-lg">{d.code}</code>
                            <span className="font-sans text-sm text-[#2D231E]">-{d.percentage.toFixed(0)}%</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${d.code_type === 'new_customer' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>
                              {d.code_type === 'new_customer' ? 'Nuevos clientes' : 'Cualquiera'}
                            </span>
                            {d.min_order_amount > 0 && (
                              <span className="text-xs text-[#5C4D43]">Min. {d.min_order_amount.toFixed(2)} EUR</span>
                            )}
                            {!d.active && <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full">Inactivo</span>}
                          </div>
                          {d.description && <p className="text-sm text-[#5C4D43] italic mt-1">{d.description}</p>}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleDiscountCode(d.id)}
                            data-testid={`toggle-discount-${d.id}`}
                          >
                            {d.active ? 'Desactivar' : 'Activar'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-300 text-red-700 hover:bg-red-50"
                            onClick={() => deleteDiscountCode(d.id)}
                            data-testid={`delete-discount-${d.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ALLERGENS TAB */}
          <TabsContent value="allergens">
            <div className="space-y-4">
              <div className="bg-[#F5EFE6] rounded-2xl p-5 border border-[#E8DEC8] flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-[#5C4D43]">
                  <p className="font-serif text-lg font-semibold text-[#2D231E] mb-1">Matriz de Alergenos (UE 1169/2011)</p>
                  <p>Marca los alergenos presentes como ingrediente en cada plato. Los clientes pueden consultar la matriz publica en <a href="/alergenos" target="_blank" rel="noopener noreferrer" className="text-[#C44536] underline">/alergenos</a>.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={() => window.open(`${API}/admin/allergens/export/xlsx`, '_blank')}
                    className="btn-primary"
                    data-testid="export-allergens-xlsx-btn"
                    title="Descargar carta de alergenos en Excel (imprimible)"
                  >
                    <Download className="w-4 h-4 mr-1" /> Carta XLSX
                  </Button>
                  <Button onClick={exportAllergenMatrix} variant="outline" data-testid="export-allergens-btn" title="Descargar JSON con la matriz actual (para sincronizar entre instalaciones)">
                    Exportar JSON
                  </Button>
                  <Button onClick={() => document.getElementById('allergen-import-file')?.click()} variant="outline" data-testid="import-allergens-btn" title="Subir JSON exportado de otra instalacion">
                    Importar JSON
                  </Button>
                  <input
                    id="allergen-import-file"
                    type="file"
                    accept="application/json,.json"
                    onChange={importAllergenMatrix}
                    className="hidden"
                    data-testid="import-allergens-file"
                  />
                  <Button onClick={saveAllergenMatrix} disabled={allergenSaving} className="btn-primary" data-testid="save-allergens-btn">
                    {allergenSaving ? 'Guardando…' : 'Guardar cambios'}
                  </Button>
                </div>
              </div>

              {/* Filters */}
              <div className="flex flex-wrap gap-2 items-center">
                <Input
                  type="search"
                  placeholder="Buscar plato..."
                  value={allergenSearch}
                  onChange={(e) => setAllergenSearch(e.target.value)}
                  className="max-w-xs bg-white border-[#E8DEC8]"
                  data-testid="allergen-search"
                />
                <select
                  value={allergenCategory}
                  onChange={(e) => setAllergenCategory(e.target.value)}
                  className="bg-white border border-[#E8DEC8] rounded-md px-3 py-2 text-sm font-sans"
                  data-testid="allergen-category-filter"
                >
                  {['Todos', ...Array.from(new Set(allergenItems.map(i => i.category)))].map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div className="overflow-x-auto rounded-2xl border border-[#E8DEC8] bg-white shadow-sm">
                <table className="w-full text-sm" data-testid="allergens-matrix">
                  <thead className="bg-[#F5EFE6] sticky top-0">
                    <tr>
                      <th className="text-left py-3 px-3 sticky left-0 bg-[#F5EFE6] z-10 min-w-[200px] font-serif">Plato</th>
                      {allergens.map(a => (
                        <th key={a.key} className="py-3 px-1 text-center font-sans text-xs whitespace-nowrap" title={a.label}>
                          <div className="flex flex-col items-center gap-0.5">
                            <span className="text-lg" aria-hidden="true">{a.icon}</span>
                            <span className="text-[10px] font-normal text-[#5C4D43]">{a.label.length > 9 ? a.label.slice(0, 8) + '…' : a.label}</span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {allergenItems
                      .filter(i => allergenCategory === 'Todos' || i.category === allergenCategory)
                      .filter(i => !allergenSearch.trim() || i.name.toLowerCase().includes(allergenSearch.toLowerCase()))
                      .map((item, idx) => (
                        <tr key={item.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#FDFBF7]'}>
                          <td className="py-2 px-3 sticky left-0 bg-inherit z-10">
                            <div className="font-sans text-[#2D231E]">{item.name}</div>
                            <div className="text-xs text-[#8A7A6D]">{item.category}</div>
                          </td>
                          {allergens.map(a => {
                            const checked = (allergenMatrix[item.id] || []).includes(a.key);
                            return (
                              <td key={a.key} className="py-2 px-1 text-center">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleAllergen(item.id, a.key)}
                                  className="w-4 h-4 accent-[#C44536] cursor-pointer"
                                  data-testid={`allergen-${item.id}-${a.key}`}
                                  aria-label={`${item.name} contiene ${a.label}`}
                                />
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* TICKETS TAB (VeriFactu prep) */}
          <TabsContent value="tickets">
            <div className="space-y-4">
              <div className="bg-[#F5EFE6] rounded-2xl p-5 border border-[#E8DEC8]">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="text-sm text-[#5C4D43] max-w-2xl">
                    <p className="font-serif text-lg font-semibold text-[#2D231E] mb-1 flex items-center gap-2">
                      <ShieldCheck className="w-5 h-5 text-[#2C5E3E]" /> Facturación / VeriFactu (RD 1007/2023)
                    </p>
                    <p>
                      Cada pedido aceptado genera un <strong>ticket inmutable</strong> con encadenamiento SHA-256, numeración secuencial y XML formato AEAT. Modo actual: <strong>{fiscal.mode === 'verifactu' ? 'VeriFactu (envío inmediato)' : 'No-VeriFactu (archivo accesible)'}</strong>.
                    </p>
                    {!fiscal.configured && (
                      <p className="mt-2 text-[#C44536] font-medium">
                        ⚠ Falta configurar NIF y Razon Social en <button type="button" className="underline" onClick={() => { setActiveTab('settings'); fetchFiscalSettings(); }}>Ajustes</button>. Sin estos datos no se emiten tickets.
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={verifyChain} variant="outline" data-testid="verify-chain-btn">
                      <ShieldCheck className="w-4 h-4 mr-1.5" /> Verificar cadena
                    </Button>
                    <Button onClick={exportTicketsXml} variant="outline" data-testid="export-tickets-xml-btn" disabled={tickets.length === 0}>
                      <Download className="w-4 h-4 mr-1.5" /> Exportar XML
                    </Button>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-3 items-center text-xs text-[#5C4D43]">
                  <span>Próximo número: <strong>T{ticketChain.year}/{String(ticketChain.next_seq || 1).padStart(5, '0')}</strong></span>
                  {chainStatus && (
                    <span className={chainStatus.ok ? 'text-[#2C5E3E]' : 'text-[#C44536]'}>
                      • Cadena: {chainStatus.ok ? `íntegra (${chainStatus.total})` : `ROTA en ${chainStatus.broken_at}`}
                    </span>
                  )}
                </div>
              </div>

              {/* Date filters */}
              <div className="flex flex-wrap gap-2 items-center">
                <Input type="date" value={ticketsDateFrom} onChange={(e) => setTicketsDateFrom(e.target.value)} className="w-auto bg-white" data-testid="tickets-date-from" />
                <span className="text-[#5C4D43]">→</span>
                <Input type="date" value={ticketsDateTo} onChange={(e) => setTicketsDateTo(e.target.value)} className="w-auto bg-white" data-testid="tickets-date-to" />
                <Button size="sm" onClick={fetchTickets} variant="outline" data-testid="apply-tickets-filter">Aplicar</Button>
                {(ticketsDateFrom || ticketsDateTo) && (
                  <Button size="sm" variant="outline" onClick={() => { setTicketsDateFrom(''); setTicketsDateTo(''); setTimeout(fetchTickets, 0); }}>Limpiar</Button>
                )}
              </div>

              {tickets.length === 0 ? (
                <div className="text-center py-12 bg-[#F5EFE6] rounded-2xl">
                  <FileText className="w-12 h-12 text-[#E8DEC8] mx-auto mb-3" />
                  <p className="font-sans text-[#5C4D43]">No hay tickets emitidos en este rango</p>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-2xl border border-[#E8DEC8] bg-white shadow-sm">
                  <table className="w-full text-sm" data-testid="tickets-table">
                    <thead className="bg-[#F5EFE6]">
                      <tr>
                        <th className="text-left py-3 px-3 font-serif">Numero</th>
                        <th className="text-left py-3 px-3 font-serif">Fecha</th>
                        <th className="text-left py-3 px-3 font-serif">Cliente</th>
                        <th className="text-right py-3 px-3 font-serif">Base</th>
                        <th className="text-right py-3 px-3 font-serif">IVA</th>
                        <th className="text-right py-3 px-3 font-serif">Total</th>
                        <th className="text-center py-3 px-3 font-serif">Hash</th>
                        <th className="text-center py-3 px-3 font-serif"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {tickets.map((t, idx) => (
                        <tr key={t.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#FDFBF7]'} data-testid={`ticket-row-${t.full_number}`}>
                          <td className="py-2 px-3 font-mono text-[#2D231E]">{t.full_number}</td>
                          <td className="py-2 px-3 text-[#5C4D43]">{t.issued_date_es}</td>
                          <td className="py-2 px-3 text-[#5C4D43]">{t.customer_name || '-'}</td>
                          <td className="py-2 px-3 text-right">{Number(t.vat?.base || 0).toFixed(2)}€</td>
                          <td className="py-2 px-3 text-right">{Number(t.vat?.cuota || 0).toFixed(2)}€ <span className="text-xs text-[#8A7A6D]">({t.vat?.tipo}%)</span></td>
                          <td className="py-2 px-3 text-right font-semibold">{Number(t.total).toFixed(2)}€</td>
                          <td className="py-2 px-3 text-center">
                            <span className="font-mono text-xs text-[#8A7A6D]" title={t.current_hash}>{t.current_hash?.slice(0, 8)}…</span>
                          </td>
                          <td className="py-2 px-3 text-center">
                            <Button size="sm" variant="ghost" onClick={() => viewTicketDetail(t)} data-testid={`view-ticket-${t.full_number}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Detail modal */}
              {selectedTicket && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedTicket(null)}>
                  <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-serif text-xl font-bold">Ticket {selectedTicket.full_number}</h3>
                      <Button size="sm" variant="ghost" onClick={() => setSelectedTicket(null)}>✕</Button>
                    </div>
                    <div className="space-y-2 text-sm font-sans">
                      <p><strong>Hash actual:</strong> <span className="font-mono text-xs break-all">{selectedTicket.current_hash}</span></p>
                      <p><strong>Hash anterior:</strong> <span className="font-mono text-xs break-all">{selectedTicket.prev_hash || '(genesis)'}</span></p>
                      <p><strong>Total:</strong> {Number(selectedTicket.total).toFixed(2)}€ ({Number(selectedTicket.vat?.base).toFixed(2)} base + {Number(selectedTicket.vat?.cuota).toFixed(2)} IVA {selectedTicket.vat?.tipo}%)</p>
                      <p><strong>QR AEAT:</strong> <a href={selectedTicket.qr_url} target="_blank" rel="noopener noreferrer" className="text-[#C44536] underline break-all">{selectedTicket.qr_url}</a></p>
                      <details className="mt-3">
                        <summary className="cursor-pointer text-[#C44536] font-medium">Ver XML formato AEAT</summary>
                        <pre className="mt-2 p-3 bg-[#F5EFE6] rounded-lg text-xs overflow-x-auto whitespace-pre-wrap">{selectedTicket.xml_payload}</pre>
                      </details>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* SETTINGS TAB */}
          <TabsContent value="settings">
            <div className="bg-[#F5EFE6] rounded-2xl p-6 border border-[#E8DEC8] max-w-lg">
              <h2 className="font-serif text-lg font-semibold text-[#2D231E] mb-6">Ajustes de Pedidos</h2>
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-sans font-semibold text-[#2D231E]">Pedidos Online</p>
                    <p className="font-sans text-sm text-[#5C4D43]">Pago con tarjeta (Stripe)</p>
                  </div>
                  <Switch
                    checked={settings.online_ordering_enabled}
                    onCheckedChange={(v) => updateSetting('online_ordering_enabled', v)}
                    data-testid="toggle-online"
                  />
                </div>
                <Separator className="bg-[#E8DEC8]" />
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-sans font-semibold text-[#2D231E]">Pedidos WhatsApp</p>
                    <p className="font-sans text-sm text-[#5C4D43]">Pedidos via WhatsApp</p>
                  </div>
                  <Switch
                    checked={settings.whatsapp_ordering_enabled}
                    onCheckedChange={(v) => updateSetting('whatsapp_ordering_enabled', v)}
                    data-testid="toggle-whatsapp"
                  />
                </div>
              </div>
            </div>

            {/* Datos fiscales (VeriFactu prep) */}
            <div className="bg-white rounded-2xl p-6 border border-[#E8DEC8] max-w-2xl mt-6" data-testid="fiscal-settings">
              <h2 className="font-serif text-lg font-semibold text-[#2D231E] mb-1 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-[#2C5E3E]" /> Datos Fiscales (Facturación)
              </h2>
              <p className="font-sans text-sm text-[#5C4D43] mb-4">
                Estos datos aparecen en cada ticket emitido y son obligatorios para cumplir con el RD 1007/2023 (VeriFactu). Sin NIF y razón social, no se emiten tickets.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="font-sans text-sm text-[#5C4D43] mb-1 block">NIF / CIF *</label>
                  <Input value={fiscal.nif} onChange={(e) => setFiscal({ ...fiscal, nif: e.target.value.toUpperCase() })} placeholder="B12345678" data-testid="fiscal-nif" />
                </div>
                <div>
                  <label className="font-sans text-sm text-[#5C4D43] mb-1 block">Razón Social *</label>
                  <Input value={fiscal.razon_social} onChange={(e) => setFiscal({ ...fiscal, razon_social: e.target.value })} placeholder="Restaurante Taj Mahal SL" data-testid="fiscal-razon" />
                </div>
                <div className="md:col-span-2">
                  <label className="font-sans text-sm text-[#5C4D43] mb-1 block">Dirección fiscal</label>
                  <Input value={fiscal.direccion} onChange={(e) => setFiscal({ ...fiscal, direccion: e.target.value })} placeholder="Calle Real 109" data-testid="fiscal-direccion" />
                </div>
                <div>
                  <label className="font-sans text-sm text-[#5C4D43] mb-1 block">Código Postal</label>
                  <Input value={fiscal.codigo_postal} onChange={(e) => setFiscal({ ...fiscal, codigo_postal: e.target.value })} placeholder="11100" data-testid="fiscal-cp" />
                </div>
                <div>
                  <label className="font-sans text-sm text-[#5C4D43] mb-1 block">Municipio</label>
                  <Input value={fiscal.municipio} onChange={(e) => setFiscal({ ...fiscal, municipio: e.target.value })} placeholder="San Fernando" data-testid="fiscal-municipio" />
                </div>
                <div>
                  <label className="font-sans text-sm text-[#5C4D43] mb-1 block">Provincia</label>
                  <Input value={fiscal.provincia} onChange={(e) => setFiscal({ ...fiscal, provincia: e.target.value })} placeholder="Cádiz" data-testid="fiscal-provincia" />
                </div>
                <div className="md:col-span-2">
                  <label className="font-sans text-sm text-[#5C4D43] mb-1 block">Modo de funcionamiento</label>
                  <select
                    value={fiscal.mode}
                    onChange={(e) => setFiscal({ ...fiscal, mode: e.target.value })}
                    className="w-full bg-white border border-[#E8DEC8] rounded-md px-3 py-2 text-sm font-sans"
                    data-testid="fiscal-mode"
                  >
                    <option value="no-verifactu">No-VeriFactu (archivo accesible para inspección)</option>
                    <option value="verifactu">VeriFactu (envío inmediato a AEAT — requiere certificado)</option>
                  </select>
                </div>
              </div>
              <Button onClick={saveFiscalSettings} disabled={fiscalSaving} className="btn-primary mt-4" data-testid="save-fiscal-btn">
                {fiscalSaving ? 'Guardando…' : 'Guardar datos fiscales'}
              </Button>
              <p className="mt-3 text-xs text-[#8A7A6D]">
                Fase 1 (preparación): los tickets se emiten con encadenamiento hash y XML formato AEAT. La fecha límite oficial es <strong>1 julio 2027 para autónomos</strong>. Cuando obtengas tu certificado FNMT, contáctame para activar la Fase 2 (firma + envío AEAT).
              </p>
            </div>

            {/* Danger zone: one-time data cleanup before go-live */}
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 max-w-lg mt-6">
              <h2 className="font-serif text-lg font-semibold text-red-800 mb-2">Zona peligrosa</h2>
              <p className="font-sans text-sm text-red-700 mb-4">
                Borra todos los pedidos de prueba, transacciones y carritos. Usa esto
                UNA sola vez antes de abrir al publico. No se puede deshacer.
              </p>
              <Button
                variant="outline"
                className="border-red-400 text-red-700 hover:bg-red-100"
                onClick={async () => {
                  if (!window.confirm('¿Borrar TODOS los pedidos, pagos y carritos? Esta accion no se puede deshacer.')) return;
                  try {
                    const res = await axios.post(`${API}/admin/reset-test-data`, {}, getAuthHeaders());
                    toast.success(
                      `Borrados: ${res.data.orders_deleted} pedidos, ${res.data.payment_transactions_deleted} pagos, ${res.data.carts_deleted} carritos`
                    );
                    fetchOrders();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Error al borrar');
                  }
                }}
                data-testid="reset-test-data-btn"
              >
                Borrar datos de prueba
              </Button>
            </div>
          </TabsContent>

          {/* PAGOS PENDIENTES TAB */}
          <TabsContent value="payments">
            <div className="space-y-6">
              {/* Recover by Redsys ref */}
              <div className="bg-[#F5EFE6] rounded-2xl p-4 border border-[#E8DEC8] space-y-3">
                <div className="flex items-start gap-2">
                  <ShieldCheck className="w-5 h-5 text-[#C44536] mt-0.5" />
                  <div>
                    <h3 className="font-serif text-lg font-semibold text-[#2D231E]">
                      Recuperar pedido por referencia Redsys
                    </h3>
                    <p className="font-sans text-xs text-[#5C4D43] mt-1">
                      Si un cliente pago con tarjeta pero no ves el pedido en la cola, verifica primero en el portal Redsys (Consultas → Operaciones) que la transaccion esta autorizada. Luego pega la referencia aqui.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    value={recoverRef}
                    onChange={(e) => setRecoverRef(e.target.value.toUpperCase())}
                    placeholder="ej. 182485C20D8E"
                    maxLength={12}
                    className="bg-white font-mono tracking-widest"
                    data-testid="recover-ref-input"
                  />
                  <Button
                    onClick={recoverByRedsysRef}
                    className="bg-[#C44536] hover:bg-[#A8392C] text-white"
                    data-testid="recover-ref-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" /> Confirmar pago
                  </Button>
                </div>
              </div>

              {/* Pending payment list */}
              <div className="flex items-center justify-between">
                <h3 className="font-serif text-lg font-semibold text-[#2D231E]">
                  Pedidos esperando confirmacion de pago
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchPendingPayments}
                  disabled={pendingLoading}
                  data-testid="refresh-pending-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-1 ${pendingLoading ? 'animate-spin' : ''}`} /> Actualizar
                </Button>
              </div>

              {pendingPayments.length === 0 ? (
                <div className="text-center py-8 bg-white rounded-xl border border-[#E8DEC8]">
                  <CheckCircle className="w-8 h-8 mx-auto text-green-600 mb-2" />
                  <p className="font-sans text-[#5C4D43]">Sin pedidos pendientes de confirmar el pago.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingPayments.map((o) => (
                    <div key={o.id} className="bg-white rounded-xl p-4 border border-[#E8DEC8] flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-baseline gap-2">
                          <span className="font-serif font-semibold text-[#2D231E]">
                            #{o.id?.substring(0, 8).toUpperCase()}
                          </span>
                          <span className="font-mono text-xs bg-[#F5EFE6] text-[#5C4D43] px-2 py-0.5 rounded">
                            {o.redsys_order_ref || '—'}
                          </span>
                          <span className="font-serif text-lg font-bold text-[#C44536]">
                            {Number(o.total || 0).toFixed(2)} EUR
                          </span>
                        </div>
                        <div className="font-sans text-sm text-[#5C4D43] mt-1">
                          {o.customer_name} · {o.customer_phone}
                        </div>
                        <div className="font-sans text-xs text-[#8A7A6D] mt-0.5">
                          {new Date(o.created_at).toLocaleString('es-ES')}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => confirmPendingPayment(o.id)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`confirm-pending-${o.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" /> Confirmar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => discardPendingPayment(o.id)}
                          className="border-red-600 text-red-600 hover:bg-red-50"
                          data-testid={`discard-pending-${o.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" /> Descartar
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
